import React, { useEffect, useState } from 'react';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormDateFields from '../components/FormDateFields';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { logEvent } from '../utils/analytics';
import { useFormikContext } from 'formik';
import { EVENT_CATEGORIES } from '../constants/events';
import FormTextField from '../components/FormTextField';
import FormCheckBox from '../components/FormCheckBox';
import FormSelect from '../components/FormSelect';
import { useTranslation } from 'react-i18next';
import { OrderingSelectors } from 'polygon-ordering';
import { useAppSelector } from '../app/hooks';
import { handleMobileKeyDown } from '../utils/mobileKeyboardValidation';

const { getMember } = OrderingSelectors;

const ProfileFields = ({
  field,
  disabled = false,
  defaultValue = '',
}: {
  field: FormikFields;
  disabled?: boolean;
  defaultValue?: string;
}) => {
  const formik = useFormikContext<FormikFields>();
  const { t } = useTranslation();
  const member = useAppSelector(getMember);
  const { loyaltyRequiresVerification, allowEmailEditTillVerify, allowVerificationEmailEdit } =
    useAppSelector(state => ({
      loyaltyRequiresVerification: state.config.loyaltyRequiresVerification,
      allowEmailEditTillVerify: state.config.allowEmailEditTillVerify,
      allowVerificationEmailEdit: state.config.allowVerificationEmailEdit,
    }));
  const [disableField, setDisableField] = useState<boolean>(disabled);
  const [canEditEmail, setCanEditEmail] = useState<boolean>(disabled);

  useEffect(() => {
    setCanEditEmail(
      !loyaltyRequiresVerification ||
        (allowEmailEditTillVerify && !member?.verified) ||
        allowVerificationEmailEdit,
    );
  }, [
    disableField,
    canEditEmail,
    allowVerificationEmailEdit,
    allowEmailEditTillVerify,
    member,
    loyaltyRequiresVerification,
  ]);

  useEffect(() => {
    setDisableField(
      disabled ||
        field.mode === 'display' ||
        (field.mode === 'once' &&
          Boolean(field.appAlias && member?.[field.appAlias as keyof Member])),
    );
  }, [disabled, member, field]);

  const { values, handleBlur, handleChange } = formik;

  const renderFormField = () => {
    const { inputType, label } = field;
    const isMobileField = field.id === 'Mobile' || field.appAlias === 'mobileNumber';

    if (field.editMode !== 'hidden') {
      switch (inputType) {
        case 'email':
          return (
            <FormControl key={field.id} fullWidth>
              <FormTextField
                disabled={disableField || !canEditEmail}
                name={field.id!}
                type="email"
                required
                label={t(`field.updateProfile.${field.id}`)}
                onChange={handleChange}
                defaultValue={defaultValue}
              />
            </FormControl>
          );
        case 'number':
          return (
            <FormControl key={field.id} fullWidth>
              <FormTextField
                name={field.id!}
                type="number"
                required
                label={t(`field.updateProfile.${field.id}`)}
                onChange={handleChange}
                disabled={disableField}
              />
            </FormControl>
          );
        case 'text':
          return (
            <FormControl key={field.id} fullWidth>
              <FormTextField
                name={field.id!}
                type="text"
                label={t(`field.updateProfile.${field.id}`)}
                onChange={handleChange}
                onKeyDown={isMobileField ? handleMobileKeyDown : undefined}
                disabled={disableField}
              />
            </FormControl>
          );
        case 'password':
          return (
            <FormControl key={field.id} fullWidth>
              <FormTextField
                name={field.id!}
                // type={inputType === 'number' ? 'digits' : field.inputType!}
                type={field.inputType!}
                onChange={handleChange}
                label={label!}
                required={field.required || field.mode === 'required'}
                disabled={disableField}
              />
            </FormControl>
          );
        case 'sendemail':
        case 'sendsms':
        case 'terms':
        case 'checkbox':
          return (
            <FormControl key={field.id} fullWidth>
              <FormCheckBox
                required={field.required || field.mode === 'required'}
                onChange={handleChange}
                name={field.id!}
                disabled={disableField}
                inputType={inputType}
                label={t(`field.updateProfile.${field.id}`)}
              />
            </FormControl>
          );
        case 'radio':
          return (
            <FormControl key={field.id} fullWidth>
              <RadioGroup
                id={field.id}
                name={field.id}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values[field.id as keyof FormikFields]}
              >
                {field.possibleValues?.map(({ value, label }: any, index: number) => (
                  <FormControlLabel
                    key={index}
                    value={value}
                    control={<Radio disabled={disableField} />}
                    label={label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          );
        case 'select':
          return (
            <FormControl key={field.id} fullWidth>
              <FormSelect
                required={field.required || field.mode === 'required'}
                label={t(`field.updateProfile.${field.id}`)}
                name={field.id!}
                onChange={handleChange}
                disabled={disableField}
                menuItems={field?.possibleValues}
              />
            </FormControl>
          );
        case 'date':
        case 'datetime':
        case 'time':
          return (
            <FormControl key={field.id} fullWidth>
              <FormDateFields
                type={field.inputType!}
                disabled={disableField}
                required={field.required || field.mode === 'required'}
                name={field.id!}
                label={t(`field.updateProfile.${field.id}`)}
                onChange={handleChange}
              />
            </FormControl>
          );
        default:
          logEvent(
            { action: 'noComponentForField', category: EVENT_CATEGORIES.FAILURE },
            { label: 'noComponentForField', value: JSON.stringify(field) },
          );
          return null;
      }
    }
  };

  return <div>{renderFormField()}</div>;
};

export default ProfileFields;
